import { jsx as _jsx } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import { memo, useCallback, useMemo } from "react";
import { usePublicPermissions } from "~/components/PublicPermissionsProvider";
import { CustomFieldTableHeader } from "~/components/TableView/CustomFieldTableHeader/CustomFieldTableHeader";
import { SelectableTableView } from "~/components/TableView/SelectableTableView";
import { TableHeaders } from "~/components/TableView/TableHeaders";
import { TablePageContainer } from "~/components/TableView/TablePageContainer";
import { usePublicTableRows } from "~/components/TableView/usePublicTableRows";
import { useTableSelectableItems } from "~/components/TableView/useTableSelectableItems";
import { BOARD_PAGE_TABLE_VIEW } from "~/constants/testIDs";
import { useTableHeaderPosition } from "~/hooks/useTableHeaderPosition";
import { useWorkspaceHorizontalPadding } from "~/hooks/useWorkspaceHorizontalPadding";
import { usePublicTableViewItems } from "~/swr-hooks/gallery/tableView/usePublicTableViewItems";
import { canChangeBoardCustomFields } from "~/utils/permissions/boardPermissions";
import { canGenerateZip } from "~/utils/permissions/taskPermissions";
import { TableViewProvider } from "../TableView/TableViewContext";
import { PublicBoardNullState } from "./PublicBoardNullState";
export const PublicBoardTableView = /*#__PURE__*/ memo((param)=>{
    let { scrollElementRef } = param;
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { tableHeaderTopPos } = useTableHeaderPosition();
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const { data, hasMore, isEmpty, isLoading, isLoadingMore, loadNextPage } = usePublicTableViewItems();
    const { permissions } = usePublicPermissions();
    const canEditCustomFields = canChangeBoardCustomFields(permissions);
    const canDownload = canGenerateZip(permissions);
    /**
   * If the parent shortUrl has the `asset.canViewOriginal` or `customField.canManageApplications` permission,
   * the user can select all items (assets or boards) in the table.
   */ const canSelectItems = canDownload || canEditCustomFields;
    const rows = usePublicTableRows({
        data,
        hasMore,
        isLoading,
        isLoadingMore,
        loadMore: loadNextPage,
        isAssetSelectable: canSelectItems,
        isBoardSelectable: canSelectItems
    });
    const selectableItems = useMemo(()=>canSelectItems ? data : [], [
        canSelectItems,
        data
    ]);
    useTableSelectableItems(selectableItems);
    const hasData = !!(data === null || data === void 0 ? void 0 : data.length);
    const renderTableHeaders = useCallback((ref)=>hasData && isAboveMediumScreen && /*#__PURE__*/ _jsx(TableHeaders, {
            ref: ref,
            CustomFieldTableHeaderComponent: CustomFieldTableHeader,
            horizontalPadding: responsiveHorizontalPadding,
            topPos: tableHeaderTopPos
        }), [
        hasData,
        isAboveMediumScreen,
        responsiveHorizontalPadding,
        tableHeaderTopPos
    ]);
    return /*#__PURE__*/ _jsx(TableViewProvider, {
        children: /*#__PURE__*/ _jsx(TablePageContainer, {
            "data-testid": BOARD_PAGE_TABLE_VIEW,
            children: isEmpty && typeof document !== "undefined" ? /*#__PURE__*/ _jsx(PublicBoardNullState, {}) : /*#__PURE__*/ _jsx(SelectableTableView, {
                canScrollHorizontally: isAboveMediumScreen && hasData,
                horizontalPadding: responsiveHorizontalPadding,
                renderHeaders: renderTableHeaders,
                rows: rows,
                scrollElementRef: scrollElementRef
            })
        })
    });
});
PublicBoardTableView.displayName = "PublicBoardTableView";
